import { useState, useEffect, useCallback } from 'react';
import { logError } from '@/lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function usePushNotifications(token) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check subscription status from backend
  useEffect(() => {
    if (!token || !isSupported) {
      setIsLoading(false);
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/push/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsSubscribed(data.subscribed);
        }
      } catch (err) {
        logError('Failed to check push status:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [token, isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !token) return false;

    try {
      setIsLoading(true);

      // Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        logError('Notification permission denied');
        return false;
      }

      // Register service worker if not already registered
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }

      // Get VAPID public key from backend
      const keyRes = await fetch(`${API_URL}/api/push/vapid-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!keyRes.ok) {
        throw new Error('Failed to get VAPID key');
      }
      
      const { publicKey } = await keyRes.json();

      // Convert VAPID key to Uint8Array
      const vapidKey = urlBase64ToUint8Array(publicKey);

      // Subscribe to push service
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey
      });

      // Send subscription to backend
      const subRes = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });

      if (subRes.ok) {
        setIsSubscribed(true);
        return true;
      }
      
      return false;
    } catch (err) {
      logError('Failed to subscribe to push:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!token) return false;

    try {
      setIsLoading(true);

      // Unsubscribe from push service
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      // Notify backend
      const res = await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setIsSubscribed(false);
        return true;
      }
      
      return false;
    } catch (err) {
      logError('Failed to unsubscribe from push:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe
  };
}

// Helper: Convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper: Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
